import React, { useState, useEffect } from "react";
import { Modal, Input, message } from "antd";
import { Image } from "react-bootstrap";
import whiteLogo from "../../Assets/logo.webp"; 
import {
  CONTACT_FORM_EMAIL,
  CONTACT_FORM_ERROR_MESSAGE,
  CONTACT_FORM_SUCCESS_MESSAGE,
  submitContactForm,
} from "../../utils/contactFormSubmission";

import './ContactModal.scss';

function ContactModal({ isModalOpen, handleCancel, idPrefix = "contact-modal" }) {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [emailError, setEmailError] = useState("");
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fieldIds = {
    name: `${idPrefix}-name`,
    email: `${idPrefix}-email`,
    emailError: `${idPrefix}-email-error`,
    company: `${idPrefix}-company`,
    message: `${idPrefix}-message`,
  };

  useEffect(() => {
    if (isModalOpen) {
      setStatus(null);
      setEmailError("");
    }
  }, [isModalOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      setEmailError(validateEmail(value) ? "" : "Please enter a valid email address.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setStatus(null);
    setEmailError("");

    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    const formSubmission = new FormData();
    formSubmission.append("name", formData.name);
    formSubmission.append("email", formData.email);
    formSubmission.append("company", formData.company);
    formSubmission.append("message", formData.message);

    formSubmission.append("from_name", "Klean Leaf Website");
    formSubmission.append("subject", "New Contact Form Submission - Klean Leaf");

    setIsSubmitting(true);
    try {
      await submitContactForm(formSubmission);
      setStatus({ type: "success" });
      message.success(CONTACT_FORM_SUCCESS_MESSAGE);
      setFormData({ name: "", email: "", company: "", message: "" });
      setTimeout(() => handleCancel(), 1500);
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setStatus({ type: "error" });
      message.error(CONTACT_FORM_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Ready to try Klean Leaf?"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      className="contact-modal-wrapper"
    >
      <div className="modal-contact">
        <Image src={whiteLogo} alt="Klean Leaf logo" className="modal-logo" />
        <p className="modal-subtitle">Fill out the form and our team will follow up about private label laundry detergent sheet samples, packaging options, MOQ, and timing.</p>

        <form onSubmit={handleSubmit} className="home-contact-form">
          <label htmlFor={fieldIds.name}>Name *</label>
          <Input id={fieldIds.name} type="text" name="name" value={formData.name} onChange={handleChange} autoComplete="name" required />

          <label htmlFor={fieldIds.email}>Email *</label>
          <Input
            id={fieldIds.email}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            aria-describedby={emailError ? fieldIds.emailError : undefined}
            aria-invalid={emailError ? "true" : "false"}
            required
          />
          {emailError && <span id={fieldIds.emailError} className="error-message">{emailError}</span>}

          <label htmlFor={fieldIds.company}>Company</label>
          <Input id={fieldIds.company} type="text" name="company" value={formData.company} onChange={handleChange} autoComplete="organization" />

          <label htmlFor={fieldIds.message}>Message *</label>
          <Input.TextArea
            id={fieldIds.message}
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="message-box"
            autoComplete="off"
            required
          />

          <button type="submit" className="submit-button primary-btn" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Submit"}
          </button>
          {status && (
            <span className={`form-result ${status.type}`}>
              {status.type === "success" ? (
                CONTACT_FORM_SUCCESS_MESSAGE
              ) : (
                <>
                  We couldn’t send your message right now. Please try again, or email us directly at{" "}
                  <a href={`mailto:${CONTACT_FORM_EMAIL}`}>{CONTACT_FORM_EMAIL}</a>.
                </>
              )}
            </span>
          )}
        </form>
      </div>
    </Modal>
  );
}

export default ContactModal;
